import React, { useMemo, useState } from 'react'
import { Box, Chip, Drawer, Tab, Tabs, Typography } from '@mui/material'
import { alpha, styled, useTheme } from '@mui/material/styles'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import HistoryIcon from '@mui/icons-material/History'
import { usePatternContext } from '../contexts/PatternContext'
import { CompositeOutput } from '../../../types/composite'
import { collectCompositeIdsFromPattern } from '../../../common/utils/composite'
import CopilotPanel from '../copilot/CopilotPanel'
import useCopilotSession from '../copilot/hooks/useCopilotSession'
import useIndicatorCopilotHost from './hooks/useIndicatorCopilotHost'
import useIndicatorVersions from './hooks/useIndicatorVersions'
import { DraftStep, nodesToDraft } from './types'

// Mirrors PatternDetailView's SidePanelContainer
const PanelContainer = styled(Box)(({ theme }) => ({
  width: 380,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.custom2,
  color: theme.palette.text.primary,
  overflow: 'hidden',
}))

const PatternRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 1.5),
  backgroundColor: theme.palette.background.custom2,
  border: `1px solid ${theme.palette.background.custom4}`,
  borderRadius: '10px',
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.5),
  },
}))

interface IndicatorSidePanelProps {
  entityId: string | null
  isMobile: boolean
  mobileOpen: boolean
  onMobileClose: () => void
  copilotEnabled: boolean
  name: string
  setName: (name: string) => void
  steps: DraftStep[]
  setSteps: (steps: DraftStep[]) => void
  outputs: CompositeOutput[]
  setOutputs: (outputs: CompositeOutput[]) => void
  setScale: (scale: 'overlay' | 'oscillator') => void
}

interface IndicatorCopilotSectionProps {
  visible: boolean
  entityId: string | null
  name: string
  setName: (name: string) => void
  steps: DraftStep[]
  setSteps: (steps: DraftStep[]) => void
  outputs: CompositeOutput[]
  setOutputs: (outputs: CompositeOutput[]) => void
}

/** Owns the copilot session hooks so they only run when the AI tab exists
 * (admin gate) — kept mounted across tab switches via display:none. */
const IndicatorCopilotSection: React.FC<IndicatorCopilotSectionProps> = ({
  visible,
  ...hostArgs
}) => {
  const host = useIndicatorCopilotHost(hostArgs)
  const session = useCopilotSession(host)
  return (
    <Box
      sx={{
        display: visible ? 'flex' : 'none',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <CopilotPanel host={host} session={session} />
    </Box>
  )
}

/**
 * The indicator editor's right panel, mirroring the pattern editor's tabbed
 * side panel: an AI Builder tab (the indicator adapter of the copilot) and a
 * Patterns tab listing every pattern that uses this indicator. Both tabs show
 * from the start — a new indicator's Patterns tab is simply empty until the
 * indicator is published and used.
 */
const IndicatorSidePanel: React.FC<IndicatorSidePanelProps> = ({
  entityId,
  isMobile,
  mobileOpen,
  onMobileClose,
  copilotEnabled,
  name,
  setName,
  steps,
  setSteps,
  outputs,
  setOutputs,
  setScale,
}) => {
  const theme = useTheme()
  const { allPatterns, setEditingPattern, setEditingIndicator } = usePatternContext()
  const [tabValue, setTabValueState] = useState<'ai' | 'patterns' | 'versions'>('patterns')
  // Listener hygiene: don't subscribe to version history until the tab that
  // renders it is opened (the subscription then stays warm across switches)
  const [versionsTabOpened, setVersionsTabOpened] = useState(false)
  const setTabValue = (value: 'ai' | 'patterns' | 'versions') => {
    if (value === 'versions') setVersionsTabOpened(true)
    setTabValueState(value)
  }
  const { versions } = useIndicatorVersions(versionsTabOpened ? entityId : null)

  const usedByPatterns = useMemo(
    () =>
      entityId
        ? (allPatterns ?? []).filter((pattern: any) =>
            collectCompositeIdsFromPattern(pattern).has(entityId)
          )
        : [],
    [allPatterns, entityId]
  )

  const openPattern = (pattern: any) => {
    setEditingIndicator(null)
    setEditingPattern(pattern)
  }

  const loadVersion = (versionNumber: number) => {
    const entry = versions.find((v) => v.version === versionNumber)
    if (!entry) return
    setName(entry.def.name)
    setSteps(nodesToDraft(entry.def.nodes))
    setOutputs(entry.def.outputs.map((o) => ({ ...o, ref: { ...o.ref } })))
    setScale(entry.def.scale ?? 'oscillator')
  }

  const content = (
    <>
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={tabValue}
          onChange={(_, value) => setTabValue(value)}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            value="patterns"
            label="Patterns"
            icon={<AccountTreeIcon />}
            iconPosition="start"
            data-tour-id="indicator-patterns-tab"
          />
          {copilotEnabled && (
            <Tab
              value="ai"
              label="AI"
              icon={<SmartToyOutlinedIcon />}
              iconPosition="start"
              data-tour-id="indicator-ai-tab"
            />
          )}
          <Tab
            value="versions"
            label="Versions"
            icon={<HistoryIcon />}
            iconPosition="start"
            data-tour-id="indicator-versions-tab"
          />
        </Tabs>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* AI Builder stays MOUNTED across tab switches so the conversation
            survives; the child owns the copilot hooks so non-admins (who never
            see the tab) don't pay for a session */}
        {copilotEnabled && (
          <IndicatorCopilotSection
            visible={tabValue === 'ai'}
            entityId={entityId}
            name={name}
            setName={setName}
            steps={steps}
            setSteps={setSteps}
            outputs={outputs}
            setOutputs={setOutputs}
          />
        )}

        {tabValue === 'versions' && (
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {versions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                <HistoryIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                  No versions yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Every publish creates an immutable version you can load back into the editor.
                </Typography>
              </Box>
            ) : (
              versions.map((entry) => (
                <PatternRow key={entry.version} onClick={() => loadVersion(entry.version)}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label={`v${entry.version}`}
                      color="primary"
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {entry.def.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {entry.def.nodes.length} step{entry.def.nodes.length === 1 ? '' : 's'} ·{' '}
                    {entry.created ? new Date(entry.created).toLocaleString() : ''} · load into
                    editor
                  </Typography>
                </PatternRow>
              ))
            )}
          </Box>
        )}

        {tabValue === 'patterns' && (
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {usedByPatterns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                <AccountTreeIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                  Not used yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {entityId
                    ? 'No patterns use this indicator yet. Select it as a condition input in any pattern and it will show up here.'
                    : 'Publish this indicator, then use it in a pattern condition — the patterns will show up here.'}
                </Typography>
              </Box>
            ) : (
              usedByPatterns.map((pattern: any) => (
                <PatternRow key={pattern.id} onClick={() => openPattern(pattern)}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {pattern.name || 'Unnamed Pattern'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pattern.trigger?.chartDetails?.symbol ?? ''} · open pattern
                  </Typography>
                </PatternRow>
              ))
            )}
          </Box>
        )}
      </Box>
    </>
  )

  if (isMobile) {
    return (
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: '100%',
            height: '100%',
            backgroundColor: theme.palette.background.custom2,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {content}
      </Drawer>
    )
  }

  return <PanelContainer data-tour-id="indicator-side-panel">{content}</PanelContainer>
}

export default IndicatorSidePanel
